import { type LpStage, type LpTrace, stampNow } from "../lib/domain";

export type LpStageListener = (trace: LpTrace, stage: LpStage) => void;

/**
 * Main-thread half of a live-price trace: takes the worker's traces (stamped through `calculated`),
 * stamps `main` when the replicated grid reaches their version and `painted` on the first grid
 * refresh that started after that. Every stage is announced to listeners as it happens.
 */
export class LivePriceTracer {
  static readonly TTL_MS = 60_000;

  private appliedVersion = 0;
  private awaitingMain: { trace: LpTrace; since: number }[] = [];
  private awaitingPaint: { trace: LpTrace; since: number }[] = [];
  private listeners = new Set<LpStageListener>();

  onStage = (cb: LpStageListener) => {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  };

  receive = (traces: LpTrace[]) => {
    this.evict();
    for (const trace of traces) {
      this.emit(trace, "arrived");
      this.emit(trace, "calculated");
      if ((trace.version ?? Number.POSITIVE_INFINITY) <= this.appliedVersion) this.main(trace);
      else this.awaitingMain.push({ trace, since: performance.now() });
    }
  };

  /** The replicated grid applied the worker's `version`. */
  applied = (version: number) => {
    this.appliedVersion = Math.max(this.appliedVersion, version);
    if (this.awaitingMain.length === 0) return;
    const ready = this.awaitingMain.filter((x) => (x.trace.version ?? Number.POSITIVE_INFINITY) <= this.appliedVersion);
    this.awaitingMain = this.awaitingMain.filter((x) => !ready.includes(x));
    for (const { trace } of ready) this.main(trace);
  };

  /** A grid refresh that began at `refreshStartedAt` (`performance.now()`) has been painted. */
  painted = (refreshStartedAt: number) => {
    if (this.awaitingPaint.length === 0) return;
    const ready = this.awaitingPaint.filter((x) => x.since <= refreshStartedAt);
    this.awaitingPaint = this.awaitingPaint.filter((x) => !ready.includes(x));
    for (const { trace } of ready) this.emit({ ...trace, ts: { ...trace.ts, painted: stampNow() } }, "painted");
  };

  /** Worker rebuilt: its versions restart, so pending traces can never match again. */
  reset = () => {
    this.appliedVersion = 0;
    this.awaitingMain = [];
    this.awaitingPaint = [];
  };

  private main = (trace: LpTrace) => {
    const stamped = { ...trace, ts: { ...trace.ts, main: stampNow() } };
    this.emit(stamped, "main");
    this.awaitingPaint.push({ trace: stamped, since: performance.now() });
  };

  private evict = () => {
    const cutoff = performance.now() - LivePriceTracer.TTL_MS;
    this.awaitingMain = this.awaitingMain.filter((x) => x.since > cutoff);
    this.awaitingPaint = this.awaitingPaint.filter((x) => x.since > cutoff);
  };

  private emit = (trace: LpTrace, stage: LpStage) => {
    for (const cb of this.listeners) cb(trace, stage);
  };
}
